import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
  name: "",
  barcode: "",
  category: "",
  saleType: "unit",
  unitLabel: "unit",
  sellingPrice: "",
  costPrice: "",
  stockQuantity: "",
  lowStockAlert: "5",
  supplier: "",
  isPopular: false,
  quickButtonOrder: "0",
};

function Products() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const token = localStorage.getItem("token")?.trim();

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return products;

    return products.filter((product) => {
      const name = String(product.name || "").toLowerCase();
      const barcode = String(product.barcode || "").toLowerCase();
      const category = String(product.category || "").toLowerCase();
      const supplier = String(product.supplier || "").toLowerCase();

      return (
        name.includes(query) ||
        barcode.includes(query) ||
        category.includes(query) ||
        supplier.includes(query)
      );
    });
  }, [products, searchQuery]);

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/products`, authHeaders);
      setProducts(res.data);
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not load products.");
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...form,
        sellingPrice: Number(form.sellingPrice),
        costPrice: Number(form.costPrice),
        stockQuantity: Number(form.stockQuantity || 0),
        lowStockAlert: Number(form.lowStockAlert || 5),
        quickButtonOrder: Number(form.quickButtonOrder || 0),
      };

      if (editingId) {
        await axios.put(`${API_URL}/products/${editingId}`, payload, authHeaders);
        setMessage("Product updated successfully.");
      } else {
        await axios.post(`${API_URL}/products`, payload, authHeaders);
        setMessage("Product added successfully.");
      }

      resetForm();
      loadProducts();
   } catch (error) {
  console.error(
    "Product save error:",
    error.response?.data || error.message
  );

  setMessage(
    error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Product save failed."
  );
}
  };

  const startEdit = (product) => {
    setEditingId(product._id);

    setForm({
      name: product.name || "",
      barcode: product.barcode || "",
      category: product.category || "",
      saleType: product.saleType || "unit",
      unitLabel: product.unitLabel || "unit",
      sellingPrice: product.sellingPrice ?? "",
      costPrice: product.costPrice ?? "",
      stockQuantity: product.stockQuantity ?? "",
      lowStockAlert: product.lowStockAlert ?? "5",
      supplier: product.supplier || "",
      isPopular: product.isPopular || false,
      quickButtonOrder: product.quickButtonOrder ?? "0",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (productId) => {
    const confirmed = window.confirm(
      "Remove this product? It will be hidden but old sales history will remain."
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/products/${productId}`, authHeaders);
      setMessage("Product removed successfully.");
      loadProducts();
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not remove product.");
    }
  };

  return (
    <div style={styles.page}>
      <h1>Product Management</h1>

      {message && <p style={styles.message}>{message}</p>}

      <form onSubmit={handleSubmit} style={styles.formCard}>
        <h2>{editingId ? "Edit Product" : "Add Product"}</h2>

        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>Product Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Product name"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Barcode</label>
            <input
              name="barcode"
              value={form.barcode}
              onChange={handleChange}
              placeholder="Barcode (optional)"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Category</label>
            <input
              name="category"
              value={form.category}
              onChange={handleChange}
              placeholder="Category e.g. Noodles, Meat"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Sale Type</label>
            <select
              name="saleType"
              value={form.saleType}
              onChange={(e) => {
                const saleType = e.target.value;

                setForm((current) => ({
                  ...current,
                  saleType,
                  unitLabel: saleType === "weight" ? "kg" : "unit",
                }));
              }}
              style={styles.input}
            >
              <option value="unit">Unit item</option>
              <option value="weight">Weighted item</option>
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Unit Label</label>
            <input
              name="unitLabel"
              value={form.unitLabel}
              onChange={handleChange}
              placeholder="unit, kg, pack"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Selling Price</label>
            <input
              name="sellingPrice"
              type="number"
              step="0.01"
              value={form.sellingPrice}
              onChange={handleChange}
              placeholder="Selling price"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Cost Price</label>
            <input
              name="costPrice"
              type="number"
              step="0.01"
              value={form.costPrice}
              onChange={handleChange}
              placeholder="Cost price"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Stock Quantity</label>
            <input
              name="stockQuantity"
              type="number"
              step="0.01"
              value={form.stockQuantity}
              onChange={handleChange}
              placeholder="Stock quantity"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Low Stock Alert</label>
            <input
              name="lowStockAlert"
              type="number"
              step="0.01"
              value={form.lowStockAlert}
              onChange={handleChange}
              placeholder="Low stock alert"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Supplier</label>
            <input
              name="supplier"
              value={form.supplier}
              onChange={handleChange}
              placeholder="Supplier"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Quick Button Order</label>
            <input
              name="quickButtonOrder"
              type="number"
              value={form.quickButtonOrder}
              onChange={handleChange}
              placeholder="Quick button order"
              style={styles.input}
            />
          </div>
        </div>

        <label style={styles.checkboxRow}>
          <input
            name="isPopular"
            type="checkbox"
            checked={form.isPopular}
            onChange={handleChange}
          />
          Show this product in Quick Add / Popular buttons
        </label>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton}>
            {editingId ? "Update Product" : "Add Product"}
          </button>

          {editingId && (
            <button type="button" onClick={resetForm} style={styles.secondaryButton}>
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <section style={styles.tableCard}>
        <div style={styles.productHeader}>
          <div>
            <h2>Products</h2>
            <p style={styles.smallText}>
              Showing {filteredProducts.length} of {products.length} products
            </p>
          </div>

          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              style={styles.secondaryButton}
            >
              Clear Search
            </button>
          )}
        </div>

        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, barcode, category, or supplier"
          style={styles.searchInput}
        />

        {products.length === 0 ? (
          <p>No products yet.</p>
        ) : filteredProducts.length === 0 ? (
          <p>No product matches your search.</p>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <strong>Name</strong>
              <strong>Category</strong>
              <strong>Type</strong>
              <strong>Price</strong>
              <strong>Stock</strong>
              <strong>Quick</strong>
              <strong>Actions</strong>
            </div>

            {filteredProducts.map((product) => (
              <div key={product._id} style={styles.tableRow}>
                <div>
                  <strong>{product.name}</strong>
                  <p style={styles.smallText}>
                    {product.barcode ? `Barcode: ${product.barcode}` : "No barcode"}
                  </p>
                </div>

                <div>{product.category}</div>

                <div>
                  {product.saleType} / {product.unitLabel}
                </div>

                <div>£{Number(product.sellingPrice).toFixed(2)}</div>

                <div>
                  {product.stockQuantity} {product.unitLabel}
                </div>

                <div>
                  {product.isPopular ? `Popular #${product.quickButtonOrder || 0}` : "No"}
                </div>

                <div style={styles.rowActions}>
                  <button onClick={() => startEdit(product)} style={styles.smallButton}>
                    Edit
                  </button>

                  <button
                    onClick={() => deleteProduct(product._id)}
                    style={styles.dangerButton}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    background: "#f4f6f8",
    fontFamily: "Arial, sans-serif",
  },
  message: {
    padding: "12px",
    background: "#fff3cd",
    borderRadius: "8px",
    marginBottom: "16px",
  },
  formCard: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: "24px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  searchInput: {
    width: "100%",
    padding: "16px",
    fontSize: "17px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    marginBottom: "16px",
  },
  checkboxRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginTop: "14px",
  },
  actions: {
    marginTop: "16px",
    display: "flex",
    gap: "12px",
  },
  primaryButton: {
    padding: "14px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  secondaryButton: {
    padding: "14px 20px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    background: "white",
  },
  tableCard: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  productHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "center",
    marginBottom: "12px",
  },
  table: {
    display: "flex",
    flexDirection: "column",
  },
  tableHeader: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr",
    gap: "12px",
    padding: "12px",
    borderBottom: "2px solid #eee",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr",
    gap: "12px",
    alignItems: "center",
    padding: "12px",
    borderBottom: "1px solid #eee",
  },
  smallText: {
    margin: "4px 0 0",
    fontSize: "12px",
    color: "#666",
  },
  rowActions: {
    display: "flex",
    gap: "8px",
  },
  smallButton: {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    cursor: "pointer",
  },
  dangerButton: {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
  },
};

export default Products;